import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Headphones, Play, Pause, SkipForward, SkipBack, Volume2, Music, Loader2, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import EmbedWrapper from '@/components/EmbedWrapper';

export default function AudioPlayer({ apiUrl, backendUrl, embed = false }) {
  const [tracks, setTracks] = useState([]);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [useTTS, setUseTTS] = useState(false);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const analyserRef = useRef(null);
  const contextRef = useRef(null);
  const sourceRef = useRef(null);

  const fetchTracks = useCallback(async () => {
    try {
      // Fetch audio files + audio-trigger documents
      const [audioRes, triggerRes] = await Promise.all([
        axios.get(`${apiUrl}/documents`, { params: { type: 'audio', page_size: 100 } }),
        axios.get(`${apiUrl}/documents/audio-triggers`).catch(() => ({ data: { documents: [] } })),
      ]);
      const audioFiles = (audioRes.data.documents || []).map(d => ({ ...d, playMode: 'file' }));
      const triggerDocs = (triggerRes.data.documents || []).map(d => ({ ...d, playMode: 'tts' }));
      setTracks([...audioFiles, ...triggerDocs]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiUrl]);

  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  const track = tracks[current];
  const isFileTrack = track?.playMode === 'file';
  const audioUrl = isFileTrack ? `${backendUrl}/api/uploads/${track.file_path}` : null;

  useEffect(() => {
    if (audioRef.current && audioUrl && isFileTrack) {
      audioRef.current.src = audioUrl;
      if (playing) audioRef.current.play().catch(() => {});
    }
  }, [audioUrl, current, isFileTrack]);

  const togglePlay = () => {
    if (!track) return;
    if (track.playMode === 'tts') {
      if (playing) { speechSynthesis.cancel(); setPlaying(false); return; }
      const text = track.description || track.title;
      const utterance = new SpeechSynthesisUtterance(text.slice(0, 3000));
      utterance.rate = 0.95;
      utterance.onend = () => setPlaying(false);
      speechSynthesis.speak(utterance);
      setPlaying(true);
      return;
    }
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play().catch(() => {}); initVisualizer(); }
    setPlaying(!playing);
  };

  const skip = (dir) => {
    speechSynthesis.cancel();
    const next = (current + dir + tracks.length) % tracks.length;
    setCurrent(next);
    setPlaying(false);
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
    setDuration(audioRef.current.duration || 0);
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (audioRef.current && duration) audioRef.current.currentTime = pct * duration;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const initVisualizer = () => {
    if (analyserRef.current || !audioRef.current || !canvasRef.current) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      contextRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      const source = ctx.createMediaElementSource(audioRef.current);
      sourceRef.current = source;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      drawWaveform();
    } catch (e) { /* already connected */ }
  };

  const drawWaveform = () => {
    if (!analyserRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const data = new Uint8Array(bufferLength);
    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(data);
      ctx.fillStyle = 'rgba(10, 10, 15, 0.85)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const barW = (canvas.width / bufferLength) * 2.5;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const barH = (data[i] / 255) * canvas.height * 0.8;
        const ratio = i / bufferLength;
        ctx.fillStyle = `rgba(212, 175, 55, ${0.5 + ratio * 0.5})`;
        ctx.fillRect(x, canvas.height - barH, barW - 1, barH);
        x += barW;
      }
    };
    draw();
  };

  useEffect(() => { return () => { if (animRef.current) cancelAnimationFrame(animRef.current); speechSynthesis.cancel(); }; }, []);

  const content = (
    <div className="min-h-screen text-slate-200" style={{ background: '#0a0a0f' }} data-testid="audio-player">
      <audio ref={audioRef} onTimeUpdate={onTimeUpdate} onEnded={() => skip(1)} crossOrigin="anonymous" />

      <div className={embed ? 'px-4 pt-4 pb-8' : 'max-w-4xl mx-auto px-4 pt-8 pb-12'}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <Headphones className="w-5 h-5" style={{ color: '#d4af37' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Federation Audio Library</h1>
            <p className="text-xs text-slate-500">{tracks.length} tracks available</p>
          </div>
        </div>

        {/* Waveform */}
        <div className="rounded-xl overflow-hidden mb-4" style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.15)' }}>
          <canvas ref={canvasRef} width={800} height={120} className="w-full h-[120px]" />
        </div>

        {/* Now Playing */}
        {track && (
          <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.15)' }}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-white truncate flex-1">{track.title}</span>
              {track.playMode === 'tts' && (
                <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(212,175,55,0.2)', color: '#d4af37', border: '1px solid rgba(212,175,55,0.3)' }}>
                  <BookOpen className="w-2.5 h-2.5 inline mr-1" />Read Aloud
                </span>
              )}
            </div>
            <div className="text-[10px] text-slate-500 mb-3">{track.category}</div>

            {/* Progress (file tracks only) */}
            {isFileTrack && (
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[10px] text-slate-500 w-10 text-right">{fmt(progress)}</span>
                <div className="flex-1 h-1.5 rounded-full cursor-pointer group" style={{ background: 'rgba(212,175,55,0.15)' }} onClick={seek}>
                  <div className="h-full rounded-full relative transition-all" style={{ width: duration ? `${(progress / duration) * 100}%` : '0%', background: '#d4af37' }}>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <span className="text-[10px] text-slate-500 w-10">{fmt(duration)}</span>
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <Button size="sm" variant="ghost" onClick={() => skip(-1)} className="text-slate-400 hover:text-white" data-testid="skip-back">
                <SkipBack className="w-5 h-5" />
              </Button>
              <Button size="sm" onClick={togglePlay}
                className="rounded-full w-12 h-12 text-white"
                style={{ background: '#d4af37' }}
                data-testid="play-pause">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => skip(1)} className="text-slate-400 hover:text-white" data-testid="skip-fwd">
                <SkipForward className="w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Playlist */}
        <div className="space-y-1">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: '#d4af37' }} /></div>
          ) : tracks.length === 0 ? (
            <div className="text-center py-16 text-slate-600">
              <Headphones className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No audio content available</p>
            </div>
          ) : tracks.map((t, i) => (
            <button
              key={`${t.id}-${t.playMode}`}
              onClick={() => { setCurrent(i); setPlaying(false); }}
              className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all"
              style={i === current ? { background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' } : { border: '1px solid transparent' }}
              data-testid={`track-${i}`}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={i === current ? { background: '#d4af37' } : { background: 'rgba(255,255,255,0.05)' }}>
                {i === current && playing
                  ? <Volume2 className="w-4 h-4" style={{ color: i === current ? '#0a0a0f' : '#64748b' }} />
                  : t.playMode === 'tts'
                    ? <BookOpen className="w-4 h-4" style={{ color: i === current ? '#0a0a0f' : '#64748b' }} />
                    : <Music className="w-4 h-4" style={{ color: i === current ? '#0a0a0f' : '#64748b' }} />
                }
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm truncate ${i === current ? 'text-white font-medium' : 'text-slate-300'}`}>{t.title}</div>
                <div className="text-[10px] text-slate-600">{t.category}{t.playMode === 'tts' ? ' · Text-to-Speech' : ''}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  if (embed) return <EmbedWrapper module="audio">{content}</EmbedWrapper>;
  return content;
}
